// ──────────────────────────────────────────────────────────────────────────
// SCENARIO AUTHORING KIT — the cinematic grammar.
//
// Every scenario is a declarative timeline of `ScenarioStep`s. Hand-writing
// those (as `burst-main.ts` does) is verbose and drifts in pacing/easing
// between scenarios. This module gives every scenario one shared vocabulary:
//
//   • gp()              grid → world-space helper (one source of truth)
//   • PACING            named durations so cuts feel consistent film-to-film
//   • loc()             target a fixed-position infra by id OR raw coords
//   • camera presets    establishingWide / dronePushIn / orbit180 / lowFollow
//                        / craneReveal / majesticPullback
//   • content builders  hudEvent / notification / productScreen / panel /
//                        updatePanel / removePanel
//   • act() / beat()    structural helpers — beat() = a `parallel` group
//
// Builders return plain `ScenarioStep`s, so a scenario file stays a pure
// data structure the existing `ScenarioRunner` already knows how to play,
// pause and seek. No engine changes are required by this kit.
// ──────────────────────────────────────────────────────────────────────────
import type {
  ScenarioStep,
  ScenarioDefinition,
  PanelData,
  PanelRow,
} from '../core/types';
import { TILE_WORLD_SCALE } from '../core/constants';

// === Geometry ============================================================

/** Grid cell → world-space coordinate. Each cell = TILE_WORLD_SCALE metres. */
export function gp(gx: number, gz: number): [number, number] {
  return [gx * TILE_WORLD_SCALE, gz * TILE_WORLD_SCALE];
}

/**
 * A camera/effect target. Either a fixed infrastructure id (resolved at
 * runtime to its actual placed position by CitySystem) or raw world coords.
 * Prefer infra ids — they survive procedural placement jitter.
 */
export type Target =
  | { infra: string; fallback?: [number, number] }
  | { at: [number, number] };

function loc(t: Target): { center: [number, number]; infraId?: string } {
  if ('infra' in t) return { center: t.fallback ?? [0, 0], infraId: t.infra };
  return { center: t.at };
}

/** Sugar for an infra target. */
export function infra(id: string, fallback?: [number, number]): Target {
  return { infra: id, fallback };
}
/** Sugar for a raw grid-coordinate target. */
export function at(gx: number, gz: number): Target {
  return { at: gp(gx, gz) };
}

// === Pacing ==============================================================
//
// One table of durations so every scenario breathes the same way. Tuned
// against `burst-main` (which felt right at ~140s over 6 acts).

export const PACING = {
  /** Wide establishing hold — let the audience read the world. */
  ESTABLISH: 7000,
  /** Dramatic push-in toward a target. */
  PUSH_IN: 5000,
  /** Full slow orbit around a target (≈180°). */
  ORBIT: 8000,
  /** A tracking/chase shot following a vehicle. */
  FOLLOW: 6000,
  /** A content beat (notification/panel reveal) with no big camera move. */
  BEAT: 4000,
  /** Short connective hold. */
  HOLD: 3000,
  /** The closing majestic pull-back to city overview. */
  PULLBACK: 12000,
  /** Instant — HUD/data updates that shouldn't block the timeline. */
  INSTANT: 0,
} as const;

export type Severity = 'critical' | 'warning' | 'success' | 'info';

// === Camera presets ======================================================
//
// All return a `camera` / `camera-orbit` / `camera-follow` step. Defaults
// encode the house cinematic style; override per shot when a beat needs it.

interface ShotOpts {
  zoom?: number;
  pitch?: number;
  bearing?: number;
  duration?: number;
}

/** High, wide, slightly rotated — the "here is the world" shot. */
export function establishingWide(t: Target, o: ShotOpts = {}): ScenarioStep {
  return {
    type: 'camera',
    duration: o.duration ?? PACING.ESTABLISH,
    params: { ...loc(t), zoom: o.zoom ?? 14.5, pitch: o.pitch ?? 48, bearing: o.bearing ?? -30 },
  };
}

/** Fast, steep descent onto a target — builds tension before a reveal. */
export function dronePushIn(t: Target, o: ShotOpts = {}): ScenarioStep {
  return {
    type: 'camera',
    duration: o.duration ?? PACING.PUSH_IN,
    params: { ...loc(t), zoom: o.zoom ?? 17.5, pitch: o.pitch ?? 70, bearing: o.bearing ?? -30 },
  };
}

/** Full slow orbit. `arc` degrees swept from `startBearing` (default 180°). */
export function orbit180(
  t: Target,
  o: ShotOpts & { startBearing?: number; arc?: number } = {},
): ScenarioStep {
  const start = o.startBearing ?? -30;
  const arc = o.arc ?? 180;
  return {
    type: 'camera-orbit',
    duration: o.duration ?? PACING.ORBIT,
    params: {
      ...loc(t),
      zoom: o.zoom ?? 18,
      pitch: o.pitch ?? 70,
      startBearing: start,
      endBearing: start + arc,
    },
  };
}

/** Low, close tracking shot riding behind/under a moving vehicle. */
export function lowFollow(
  vehicleId: string,
  o: { pitch?: number; zoom?: number; duration?: number } = {},
): ScenarioStep {
  return {
    type: 'camera-follow',
    duration: o.duration ?? PACING.FOLLOW,
    params: { vehicleId, pitch: o.pitch ?? 80, zoom: o.zoom ?? 19 },
  };
}

/** A rising "crane" move — pull up and back to re-establish context. */
export function craneReveal(t: Target, o: ShotOpts = {}): ScenarioStep {
  return {
    type: 'camera',
    duration: o.duration ?? PACING.PUSH_IN,
    params: { ...loc(t), zoom: o.zoom ?? 15.5, pitch: o.pitch ?? 50, bearing: o.bearing ?? 30 },
  };
}

/** The closing shot — long, slow, majestic pull-back to the city overview. */
export function majesticPullback(o: ShotOpts = {}): ScenarioStep {
  return {
    type: 'camera',
    duration: o.duration ?? PACING.PULLBACK,
    params: { center: gp(0, 0), zoom: o.zoom ?? 14, pitch: o.pitch ?? 50, bearing: o.bearing ?? -17 },
  };
}

// === Content builders ====================================================

export function hudEvent(
  icon: string,
  title: string,
  detail: string,
  severity: Severity = 'info',
): ScenarioStep {
  return { type: 'hud-event', duration: PACING.INSTANT, params: { icon, title, detail, severity } };
}

export function hudTeam(
  teams: Array<{ name: string; status: string; eta?: string; destination: string }>,
): ScenarioStep {
  return { type: 'hud-team', duration: PACING.INSTANT, params: { teams } };
}

export function hudRegion(
  regions: Array<{ name: string; status: string; properties: number }>,
): ScenarioStep {
  return { type: 'hud-region', duration: PACING.INSTANT, params: { regions } };
}

export function hudWeather(windSpeed: number, rainfall: number, pressure: number): ScenarioStep {
  return { type: 'hud-weather', duration: PACING.INSTANT, params: { windSpeed, rainfall, pressure } };
}

export function notification(
  title: string,
  message: string,
  severity: Severity = 'info',
  duration: number = PACING.BEAT,
): ScenarioStep {
  return { type: 'notification', duration, params: { title, message, severity } };
}

interface ProductScreenOpts {
  image?: string;
  component?: string;
  device?: 'desktop' | 'mobile';
  subtitle?: string;
  role?: string;
  url?: string;
  duration?: number;
}

/** A product UI beat — full-frame app screenshot or a live mock component. */
export function productScreen(title: string, o: ProductScreenOpts = {}): ScenarioStep {
  return {
    type: 'show-screen',
    duration: o.duration ?? 5500,
    params: {
      image: o.image,
      component: o.component,
      device: o.device ?? 'desktop',
      title,
      subtitle: o.subtitle,
      role: o.role,
      url: o.url,
    },
  };
}

export function panel(
  id: string,
  title: string,
  panelType: PanelData['panelType'],
  t: Target,
  data: PanelRow[],
): ScenarioStep {
  const { center, infraId } = loc(t);
  return {
    type: 'show-panel',
    duration: PACING.INSTANT,
    params: { id, title, panelType, coordinates: center, infraId, data },
  };
}

export function updatePanel(id: string, data: PanelRow[]): ScenarioStep {
  return { type: 'update-panel', duration: PACING.INSTANT, params: { id, data } };
}

export function removePanel(id: string): ScenarioStep {
  return { type: 'remove-panel', duration: 300, params: { id } };
}

export function spawnVehicle(
  vehicleId: string,
  modelId: string,
  origin: Target,
  destination: Target,
  label: string,
  travelMs: number,
): ScenarioStep {
  const o = loc(origin);
  const d = loc(destination);
  return {
    type: 'spawn-vehicle',
    duration: travelMs,
    params: {
      vehicleId,
      modelId,
      origin: o.center,
      originInfraId: o.infraId,
      destination: d.center,
      destinationInfraId: d.infraId,
      label,
    },
  };
}

export function weather(type: 'storm' | 'clear', duration = PACING.HOLD): ScenarioStep {
  return { type: 'weather', duration, params: { type } };
}

// === Combined-solution toolkit =========================================
//
// Bespoke visuals for the "One Operating Layer" story. `infra` ids resolve to
// their placed positions at runtime, same as camera/effect targets elsewhere.

/** Source jobs/demand streaming in and fusing into one hub ("3 jobs → 1 dig"). */
export function convergence(
  id: string,
  sources: Target[],
  target: Target,
  durationMs: number,
  o: { color?: string; speed?: number } = {},
): ScenarioStep {
  return {
    type: 'convergence',
    duration: durationMs,
    params: {
      id,
      sources: sources.map((s) => loc(s)).map((l) => ({ coordinates: l.center, infraId: l.infraId })),
      target: (() => { const l = loc(target); return { coordinates: l.center, infraId: l.infraId }; })(),
      color: o.color,
      speed: o.speed,
    },
  };
}

/** ORP ride-hailing radar sweep that locks onto the best-fit crew. */
export function matchBeam(
  id: string,
  hub: Target,
  candidates: Array<{ at: Target; best?: boolean }>,
  durationMs: number,
  o: { color?: string; winColor?: string; radius?: number; lockAt?: number } = {},
): ScenarioStep {
  const h = loc(hub);
  return {
    type: 'match-beam',
    duration: durationMs,
    params: {
      id,
      hub: { coordinates: h.center, infraId: h.infraId },
      candidates: candidates.map((c) => {
        const l = loc(c.at);
        return { coordinates: l.center, infraId: l.infraId, best: c.best };
      }),
      color: o.color,
      winColor: o.winColor,
      radius: o.radius,
      lockAt: o.lockAt,
    },
  };
}

/** The ORP "four dials" frame as in-world filling gauge arcs. Values 0..1. */
export function gaugeCluster(
  id: string,
  t: Target,
  gauges: Array<{ value: number; color?: string }>,
  durationMs: number,
  o: { spacing?: number; gaugeRadius?: number } = {},
): ScenarioStep {
  const l = loc(t);
  return {
    type: 'gauge-cluster',
    duration: durationMs,
    params: {
      id,
      coordinates: l.center,
      infraId: l.infraId,
      gauges,
      spacing: o.spacing,
      gaugeRadius: o.gaugeRadius,
    },
  };
}

export const dimStart: ScenarioStep = { type: 'dim-start', duration: 0, params: {} };
export const dimEnd: ScenarioStep = { type: 'dim-end', duration: 0, params: {} };
export const stormRipple = (duration = PACING.ORBIT): ScenarioStep => ({
  type: 'storm-ripple',
  duration,
  params: {},
});
export const hold = (duration = PACING.HOLD): ScenarioStep => ({ type: 'wait', duration });

// === Structure ===========================================================

/**
 * A `beat` = a `parallel` group: a camera move running while content
 * (HUD/panel/effects) lands on top of it. This is the atomic unit of the
 * house style — almost every line in a polished scenario is a beat.
 */
export function beat(durationMs: number, steps: ScenarioStep[]): ScenarioStep {
  return { type: 'parallel', duration: durationMs, params: { steps } };
}

/**
 * An `act` is just an ordered list of steps with a label comment. Kept as a
 * thin wrapper so scenario files read as a storyboard and we can later add
 * per-act instrumentation (seek targets) without touching scenario data.
 */
export function act(_label: string, steps: ScenarioStep[]): ScenarioStep[] {
  return steps;
}

/** Assemble the final definition from ordered acts. */
export function defineScenario(
  meta: Omit<ScenarioDefinition, 'steps'>,
  acts: ScenarioStep[][],
): ScenarioDefinition {
  return { ...meta, steps: acts.flat() };
}
